import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Server, HardDrive, Cpu, MemoryStick, Network, Database, CheckCircle, XCircle } from 'lucide-react';

const NodeDetailsModal = ({ nodeId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nodeDetails, setNodeDetails] = useState(null);

    useEffect(() => {
        const fetchNodeDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await axios.get(`/api/admin/nodes/${nodeId}/details`);
                setNodeDetails(response.data.node);
            } catch (error) {
                console.error('Error fetching node details:', error);
                setError(error.response?.data?.message || 'Erro ao carregar detalhes do nó');
            } finally {
                setLoading(false);
            }
        };

        fetchNodeDetails();
    }, [nodeId]);

    // Format bytes to human readable format
    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    // Format uptime to human readable format
    const formatUptime = (seconds) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        
        let result = '';
        if (days > 0) result += `${days}d `;
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0) result += `${minutes}m`;
        
        return result.trim() || '0m';
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4 pb-2 border-b">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                        <Server className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-2" />
                        Detalhes do Nó Proxmox
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[70vh]">
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    ) : nodeDetails ? (
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-blue-800 mb-2">Informações Básicas</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600">Nome:</p>
                                        <p className="font-medium">{nodeDetails.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Host:</p>
                                        <p className="font-medium">{nodeDetails.host}:{nodeDetails.port}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Nome Real:</p>
                                        <p className="font-medium">{nodeDetails.realName}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Tipo:</p>
                                        <p className="font-medium">
                                            {nodeDetails.isCluster ? 'Cluster Proxmox' : 'Nó Standalone'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* System Status */}
                            {nodeDetails.status && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Server className="h-5 w-5 text-gray-700 mr-2" />
                                        Status do Sistema
                                    </h3>
                                    <div className="bg-white border rounded-lg overflow-hidden">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4">
                                            <div className="bg-gray-50 p-3 rounded">
                                                <div className="flex items-center">
                                                    <Cpu className="h-5 w-5 text-blue-600 mr-2" />
                                                    <span className="text-sm font-medium">CPU</span>
                                                </div>
                                                <div className="mt-2">
                                                    <p className="text-sm">
                                                        <span className="font-medium">{nodeDetails.status.cpuinfo?.cores || 'N/A'}</span> cores / <span className="font-medium">{nodeDetails.status.cpuinfo?.sockets || 'N/A'}</span> sockets
                                                    </p>
                                                    <p className="text-sm">
                                                        Modelo: <span className="font-medium">{nodeDetails.status.cpuinfo?.model || 'N/A'}</span>
                                                    </p>
                                                    <p className="text-sm">
                                                        Uso: <span className="font-medium">{(nodeDetails.status.cpu * 100).toPrecision(2)}%</span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded">
                                                <div className="flex items-center">
                                                    <MemoryStick className="h-5 w-5 text-green-600 mr-2" />
                                                    <span className="text-sm font-medium">Memória</span>
                                                </div>
                                                <div className="mt-2">
                                                    <p className="text-sm">
                                                        Total: <span className="font-medium">{formatBytes(nodeDetails.status.memory.total)}</span>
                                                    </p>
                                                    <p className="text-sm">
                                                        Usado: <span className="font-medium">{formatBytes(nodeDetails.status.memory.used)}</span> ({(nodeDetails.status.memory.used / nodeDetails.status.memory.total * 100).toFixed(1)}%)
                                                    </p>
                                                    <p className="text-sm">
                                                        Livre: <span className="font-medium">{formatBytes(nodeDetails.status.memory.free)}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded">
                                                <div className="flex items-center">
                                                    <Database className="h-5 w-5 text-purple-600 mr-2" />
                                                    <span className="text-sm font-medium">Swap</span>
                                                </div>
                                                <div className="mt-2">
                                                    <p className="text-sm">
                                                        Total: <span className="font-medium">{formatBytes(nodeDetails.status.swap.total)}</span>
                                                    </p>
                                                    <p className="text-sm">
                                                        Usado: <span className="font-medium">{formatBytes(nodeDetails.status.swap.used)}</span> ({nodeDetails.status.swap.total > 0 ? (nodeDetails.status.swap.used / nodeDetails.status.swap.total * 100).toFixed(1) : 0}%)
                                                    </p>
                                                    <p className="text-sm">
                                                        Livre: <span className="font-medium">{formatBytes(nodeDetails.status.swap.free)}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded">
                                                <div className="flex items-center">
                                                    <Server className="h-5 w-5 text-indigo-600 mr-2" />
                                                    <span className="text-sm font-medium">Uptime</span>
                                                </div>
                                                <div className="mt-2">
                                                    <p className="text-sm">
                                                        Tempo ativo: <span className="font-medium">{formatUptime(nodeDetails.status.uptime)}</span>
                                                    </p>
                                                    <p className="text-sm">
                                                        Carga (1/5/15 min): <br/><span className="font-medium">
                                                            {nodeDetails.status.loadavg[0]} / {nodeDetails.status.loadavg[1]} / {nodeDetails.status.loadavg[2]}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Storage */}
                            {nodeDetails.storage && nodeDetails.storage.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <HardDrive className="h-5 w-5 text-gray-700 mr-2" />
                                        Armazenamento
                                    </h3>
                                    <div className="bg-white border rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usado</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disponível</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {nodeDetails.storage.map((store, index) => (
                                                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{store.storage}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{store.type}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{store.total ? formatBytes(store.total) : 'N/A'}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{store.used ? formatBytes(store.used) : 'N/A'}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{store.avail ? formatBytes(store.avail) : 'N/A'}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${store.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                    {store.active ? 'Ativo' : 'Inativo'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Network */}
                            {nodeDetails.network && nodeDetails.network.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Network className="h-5 w-5 text-gray-700 mr-2" />
                                        Rede
                                    </h3>
                                    <div className="bg-white border rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interface</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endereço IP</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {nodeDetails.network.map((iface, index) => (
                                                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{iface.iface}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{iface.type}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                                {iface.address ? `${iface.address}/${iface.netmask || ''}` : 'N/A'}
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${iface.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                    {iface.active ? 'Ativo' : 'Inativo'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Cluster Information */}
                            {nodeDetails.isCluster && nodeDetails.clusterNodes && nodeDetails.clusterNodes.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center">
                                        <Server className="h-5 w-5 text-gray-700 mr-2" />
                                        Informações do Cluster
                                    </h3>
                                    <div className="bg-white border rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome do Nó</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {nodeDetails.clusterNodes.map((node, index) => (
                                                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{node.name}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{node.nodeid}</td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                    node.online ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {node.online ? (
                                                                        <>
                                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                                            Online
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <XCircle className="h-3 w-3 mr-1" />
                                                                            Offline
                                                                        </>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{node.ip || 'N/A'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            Nenhuma informação disponível
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NodeDetailsModal;